/**
 * Attribute table editor — opens a paginated editable table for one
 * vector layer. The user changes cell values; on Save we PATCH each
 * dirty row through `/layers/{id}/features/{fid}`.
 *
 * Notes
 * - Geometry isn't editable here (separate feature; needs a draw library).
 * - We page in 200-row chunks. For 100k+ feature layers the editor stays
 *   responsive because we don't render the whole table at once.
 */
import { getFeatures, updateFeature, getLayer } from '../api/layers.js';
import { toast } from './toast.js';

const PAGE_SIZE = 200;

let state = null;
let userLayerManagerRef = null;

export function registerUserLayerManager(m) { userLayerManagerRef = m; }

export async function openFeaturesEditor(layerId) {
  let layer;
  try { layer = await getLayer(layerId); }
  catch (e) { toast(`Couldn't load layer: ${e.message}`); return; }

  if (layer.layer_type !== 'vector' || layer.status !== 'ready') {
    toast('Only ready vector layers are editable.');
    return;
  }

  state = {
    layer,
    page: 0,
    rows: [],          // [{fid, props}]
    columns: [],       // editable string keys
    dirty: new Map(),  // fid → { col → newValue }
    saving: false,
  };

  document.querySelector('#featuresEditorModal')?.remove();
  document.body.insertAdjacentHTML('beforeend', shellHtml(layer));
  const root = document.getElementById('featuresEditorModal');
  bindShell(root);
  await loadPage(0);
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

async function loadPage(page) {
  state.page = page;
  state.rows = [];
  state.dirty.clear();
  renderTable({ loading: true });

  try {
    const fc = await getFeatures(state.layer.id, {
      limit:  PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    state.rows = (fc.features || []).map(f => ({
      fid:   f.properties?._fid,
      props: { ...f.properties },
    })).filter(r => r.fid != null);
    state.columns = pickColumns(state.rows);
    renderTable();
  } catch (e) {
    renderTable({ error: e.message });
  }
}

function pickColumns(rows) {
  const seen = new Set();
  for (const r of rows) {
    for (const k of Object.keys(r.props)) {
      if (k === '_fid' || k === 'geometry') continue;
      seen.add(k);
    }
  }
  return [...seen];
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function shellHtml(layer) {
  return `
    <div class="modal-bg show" id="featuresEditorModal">
      <div class="modal" style="width:880px;max-width:96vw;max-height:90vh;display:flex;flex-direction:column;padding:0">
        <div style="padding:18px 22px 10px;border-bottom:1px solid var(--border);display:flex;align-items:baseline;justify-content:space-between;gap:14px">
          <div>
            <h3 style="margin:0">Edit · ${escape(layer.name)}</h3>
            <div style="font-size:11px;color:var(--text3);margin-top:3px">${(layer.feature_count ?? 0).toLocaleString()} feature${layer.feature_count === 1 ? '' : 's'} · click any cell to edit</div>
          </div>
          <button class="rpanel-close" data-fe="close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div id="feTable" style="flex:1;overflow:auto;padding:0 22px 14px;min-height:200px"></div>
        <div style="padding:12px 22px;border-top:1px solid var(--border);display:flex;align-items:center;gap:12px">
          <div id="fePager" style="font-size:11.5px;color:var(--text3);font-family:var(--mono)"></div>
          <div style="flex:1"></div>
          <button class="btn btn-sm" data-fe="prev">‹ Prev</button>
          <button class="btn btn-sm" data-fe="next">Next ›</button>
          <button class="btn btn-sm" data-fe="discard">Discard</button>
          <button class="btn btn-sm btn-primary" data-fe="save" id="feSave" disabled>Save</button>
        </div>
      </div>
      <style>
        .fe-table { width:100%; border-collapse:collapse; font-size:11.5px; font-family:var(--mono); }
        .fe-table th, .fe-table td { border-bottom:1px solid var(--border); padding:6px 10px; text-align:left; vertical-align:top; }
        .fe-table th { font-family:var(--font); font-weight:600; color:var(--text2); position:sticky; top:0; background:var(--bg2); z-index:1; font-size:10.5px; text-transform:uppercase; letter-spacing:0.08em; }
        .fe-cell { width:100%; background:transparent; border:1px solid transparent; color:var(--text); padding:4px 6px; font-family:var(--mono); font-size:11.5px; border-radius:4px; }
        .fe-cell:focus { outline:none; border-color:var(--primary); background:var(--bg3); }
        .fe-cell.dirty { background:rgba(152,194,98,0.12); border-color:rgba(152,194,98,0.4); }
        .fe-fid { color:var(--text3); }
      </style>
    </div>
  `;
}

function bindShell(root) {
  root.addEventListener('click', e => {
    if (e.target === root || e.target.closest('[data-fe="close"]')) closeEditor();
    if (e.target.closest('[data-fe="prev"]')) gotoPage(state.page - 1);
    if (e.target.closest('[data-fe="next"]')) gotoPage(state.page + 1);
    if (e.target.closest('[data-fe="discard"]')) discardChanges();
    if (e.target.closest('[data-fe="save"]')) saveChanges();
  });
  document.addEventListener('keydown', escClose, { once: true });
  function escClose(e) { if (e.key === 'Escape' && document.getElementById('featuresEditorModal')) closeEditor(); }
}

function renderTable(opts = {}) {
  const host = document.getElementById('feTable');
  if (!host) return;

  if (opts.loading) {
    host.innerHTML = `<div style="padding:30px 4px;color:var(--text3);font-style:italic">Loading…</div>`;
    return;
  }
  if (opts.error) {
    host.innerHTML = `<div class="alert alert-error">${escape(opts.error)}</div>`;
    return;
  }
  if (state.rows.length === 0) {
    host.innerHTML = `<div style="padding:30px 4px;color:var(--text3);font-style:italic">No features on this page.</div>`;
    updatePager();
    return;
  }

  const cols = state.columns;
  const head = `<tr><th class="fe-fid">_fid</th>${cols.map(c => `<th>${escape(c)}</th>`).join('')}</tr>`;
  const body = state.rows.map(r => `
    <tr data-fid="${r.fid}">
      <td class="fe-fid">${r.fid}</td>
      ${cols.map(c => {
        const v = r.props[c];
        return `<td><input class="fe-cell" data-col="${escape(c)}" value="${escape(v == null ? '' : String(v))}"></td>`;
      }).join('')}
    </tr>
  `).join('');

  host.innerHTML = `<table class="fe-table"><thead>${head}</thead><tbody>${body}</tbody></table>`;
  host.addEventListener('input', onCellInput);
  updatePager();
}

function onCellInput(e) {
  const cell = e.target.closest('.fe-cell');
  if (!cell) return;
  const tr  = cell.closest('tr');
  const fid = +tr.dataset.fid;
  const col = cell.dataset.col;
  const original = state.rows.find(r => r.fid === fid)?.props[col];
  const isDirty = String(original ?? '') !== cell.value;
  cell.classList.toggle('dirty', isDirty);

  if (!state.dirty.has(fid)) state.dirty.set(fid, {});
  const row = state.dirty.get(fid);
  if (isDirty) row[col] = cell.value;
  else delete row[col];
  if (Object.keys(row).length === 0) state.dirty.delete(fid);

  document.getElementById('feSave').disabled = state.dirty.size === 0 || state.saving;
}

function updatePager() {
  const total = state.layer.feature_count ?? 0;
  const start = state.page * PAGE_SIZE + 1;
  const end   = Math.min(start + state.rows.length - 1, total);
  document.getElementById('fePager').textContent =
    total > 0 ? `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}` : '';
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

async function gotoPage(n) {
  if (n < 0) return;
  if (state.dirty.size && !confirm('Discard unsaved edits and move to another page?')) return;
  await loadPage(n);
}

function discardChanges() {
  if (state.dirty.size === 0) { closeEditor(); return; }
  if (!confirm(`Discard ${state.dirty.size} unsaved edit${state.dirty.size === 1 ? '' : 's'}?`)) return;
  // Re-render the page from existing state, dropping cell values back to originals.
  state.dirty.clear();
  renderTable();
}

async function saveChanges() {
  if (state.dirty.size === 0 || state.saving) return;
  state.saving = true;
  document.getElementById('feSave').disabled = true;

  const updates = [...state.dirty.entries()];
  let okCount = 0;
  let firstError = null;

  for (const [fid, props] of updates) {
    try {
      const coerced = coerceTypes(state.rows.find(r => r.fid === fid)?.props, props);
      const updated = await updateFeature(state.layer.id, fid, coerced);
      if (updated?.properties) {
        const row = state.rows.find(r => r.fid === fid);
        if (row) row.props = updated.properties;
      }
      okCount++;
    } catch (e) {
      firstError = firstError || e.message;
      break;
    }
  }

  state.dirty.clear();
  state.saving = false;
  renderTable();

  if (firstError) {
    toast(`Saved ${okCount}/${updates.length} · ${firstError}`);
  } else {
    toast(`Saved ${okCount} feature${okCount === 1 ? '' : 's'}`);
    // Refresh the layer on the map so changes are visible immediately.
    try { await refreshOnMap(state.layer.id); } catch { /* ignore */ }
  }
}

/** Best-effort type coercion based on the original column value. */
function coerceTypes(original, props) {
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    const orig = original?.[k];
    if (typeof orig === 'number' && v !== '' && !Number.isNaN(Number(v))) {
      out[k] = Number(v);
    } else if (typeof orig === 'boolean') {
      out[k] = /^(true|1|yes)$/i.test(v);
    } else if (v === '' && orig === null) {
      out[k] = null;
    } else {
      out[k] = v;
    }
  }
  return out;
}

async function refreshOnMap(layerId) {
  if (!userLayerManagerRef) return;
  if (!userLayerManagerRef.isLoaded(layerId)) return;
  const layer = await getLayer(layerId);
  await userLayerManagerRef.remove(layerId);
  await userLayerManagerRef.show(layer);
}

function closeEditor() {
  if (state?.dirty.size && !confirm('Discard unsaved edits?')) return;
  document.getElementById('featuresEditorModal')?.remove();
  state = null;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
